import React, { useState, useEffect, useCallback } from "react";
import { TbCurrentLocation } from "react-icons/tb";
import { IoEyeSharp } from 'react-icons/io5';
import { FaEyeSlash } from 'react-icons/fa';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LocationPicker from "../LocationPicker/LocationPicker";

const RegCustomer = () => {
    // State initialization
    const [customerMaster, setCustomerMaster] = useState({
        outletId: "",
        customerName: "",
        contactNo: "",
        password: "",
        contactEmail: "",
        preferredAddress: "0",
        addresses: [{
            addressId: 1,
            addressType: "Home",
            addLine1: "",
            addLine2: "",
            nearBy: "",
            area: "",
            city: "",
            state: "",
            pincode: "",
            latitude: 0,
            longitude: 0,
        }],
    });

    const [formErrors, setFormErrors] = useState({
        customerName: "",
        password: "",
        confirmPassword: "",
        area: "",
        addLine1: "",
        nearBy: "",
        city: "",
        coordinates: ""
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const API_BASE_URL = process.env.REACT_APP_API_KEY;
    const navigate = useNavigate();

    // Get outlet data from localStorage
const storedData = JSON.parse(localStorage.getItem("outletdataLocal")) || {};

// Check if data exists and is an array
const outletInfo = storedData.data && Array.isArray(storedData.data) && storedData.data.length > 0
  ? storedData.data[0]
  : {};

const { outletId, outletlogo, outletName } = outletInfo;


    // Password visibility toggles
    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

    // Get current location
    const getCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by this browser.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setLatitude(latitude);
                setLongitude(longitude);
                fetchAddressFromCoordinates(latitude, longitude);
            },
            (error) => {
                const errors = {
                    [error.PERMISSION_DENIED]: "Location permission denied. Please allow access.",
                    [error.POSITION_UNAVAILABLE]: "Location information is unavailable.",
                    [error.TIMEOUT]: "Location request timed out. Try again.",
                    [error.UNKNOWN_ERROR]: "An unknown error occurred.",
                };
                alert(errors[error.code] || "Unable to fetch location.");
            },
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
    }, []);

    // Fetch address from coordinates
    const fetchAddressFromCoordinates = useCallback(async (lat, lon) => {
        try {
            const response = await axios.get("https://nominatim.openstreetmap.org/reverse", {
                params: { lat, lon, format: "json" }
            });
    
            const address = response.data.address || {};
    
            const updatedAddress = {
                
                city: address.town || address.village || address.city || "",
                state: address.state || "",
                pincode: address.postcode || "",
                latitude: lat,
                longitude: lon
            };
    
            setCustomerMaster((prev) => {
                const updatedAddresses = [...prev.addresses];
                updatedAddresses[0] = {
                    ...updatedAddresses[0],
                    ...updatedAddress
                };
                return { ...prev, addresses: updatedAddresses };
            });
    
        } catch (error) {
            console.error("Error fetching address:", error);
            alert("Error fetching address from coordinates.");
        }
    }, []);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        setCustomerMaster(prev => ({
            ...prev,
            outletId,
            [name]: value
        }));

        // Validate fields
        if (name === "customerName") {
            setFormErrors(prev => ({
                ...prev,
                customerName: value.trim().length === 0 
                    ? "This field is required" 
                    : value.trim().length < 3 
                        ? "Minimum 3 characters required" 
                        : ""
            }));
        }

        if (name === "password") {
            setFormErrors(prev => ({
                ...prev,
                password: value.length === 0 ? "Password is required" : "",
                confirmPassword: value !== confirmPassword ? "Passwords do not match" : ""
            }));
        }
    };

    // Handle confirm password change
    const handleConfirmPasswordChange = (e) => {
        const { value } = e.target;
        setConfirmPassword(value);
        
        setFormErrors(prev => ({
            ...prev,
            confirmPassword: value !== customerMaster.password ? "Passwords do not match" : ""
        }));
    };

    // Handle address changes
    const handleAddressChange = (e) => {
        const { name, value } = e.target;

        setCustomerMaster(prev => {
            const updatedAddresses = [...prev.addresses];
            updatedAddresses[0] = {
                ...updatedAddresses[0],
                [name]: value,
                latitude: latitude || updatedAddresses[0].latitude,
                longitude: longitude || updatedAddresses[0].longitude
            };
            

            return {
                ...prev,
                addresses: updatedAddresses
            };
        });

        // Clear error when user types
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    // Auto-fill password with last 5 digits of contact number
    useEffect(() => {
        if (customerMaster.contactNo?.length >= 5) {
            const lastFiveDigits = customerMaster.contactNo.slice(-5);
            setCustomerMaster(prev => ({ ...prev, password: lastFiveDigits }));
        }
    }, [customerMaster.contactNo]);

    // Store coordinates in localStorage when they change
    useEffect(() => {
        if (latitude && longitude) {
            localStorage.setItem("curLat", latitude);
            localStorage.setItem("curLon", longitude);
            setFormErrors(prev => ({ ...prev, coordinates: "" }));
        }
    }, [latitude, longitude]);

    // Form submission
    const RegisterCustomer = async () => {
        setIsSubmitting(true);
        
        // Validate all fields
        const newErrors = {
            customerName: !customerMaster.customerName ? "Customer Name is required" 
                : customerMaster.customerName.trim().length < 3 ? "Minimum 3 characters required" : "",
            password: !customerMaster.password ? "Password is required" : "",
            confirmPassword: customerMaster.password !== confirmPassword ? "Passwords do not match" : "",
            area: !customerMaster.addresses[0].area ? "Area is required" : "",
            addLine1: !customerMaster.addresses[0].addLine1 ? "Address Line 1 is required" : "",
            nearBy: !customerMaster.addresses[0].nearBy ? "Nearby landmark is required" : "",
            city: !customerMaster.addresses[0].city ? "City is required" : "",
            coordinates: !latitude || !longitude ? "Please set your location coordinates" : ""
        };

        setFormErrors(newErrors);

        // Check if any errors exist
        const hasErrors = Object.values(newErrors).some(error => error !== "");
        if (hasErrors) {
            setIsSubmitting(false);
            return;
        }

        // Prepare data for submission
        const cleanedData = {
            ...customerMaster,
            addresses: [{
                ...customerMaster.addresses[0],
                latitude,
                longitude
            }]
        };

        try {
            const response = await axios.post(`${API_BASE_URL}CustomerMaster`, cleanedData);
            const loginDetails = {
                customerId: response.data.customerId,
                preferredAddress: response.data.preferredAddress,
                customerName: response.data.customerName
            };

            localStorage.setItem("loginDetails", JSON.stringify(loginDetails));
            navigate("/QrCartItems");
        } catch (error) {
            console.error("Error posting data", error);
            alert("Registration failed. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container">
            <div className="row">
                <nav className="navbar navbar-light bg-light shadow-sm bg-body rounded">
                    <div className="container-fluid">
                        <a className="navbar-brand" href="#">
                            <img
                                src={outletlogo}
                                alt=""
                                width={60}
                                height={40}
                                className="d-inline-block align-text-center me-2 img-fluid"
                            />
                            <strong>{outletName}</strong>
                        </a>
                    </div>
                </nav>

                <div className="col-md-12 mt-2 border p-3">
                    {/* Customer Information */}
                    <div className="mb-3">
                       {/* <label className="form-label">Enter Name</label> */}
                        <input
                            type="text"
                            className={`form-control ${formErrors.customerName ? "is-invalid" : ""}`}
                            placeholder="Enter Name"
                            name="customerName"
                            value={customerMaster.customerName}
                            onChange={handleInputChange}
                        />
                        {formErrors.customerName && (
                            <div className="invalid-feedback">{formErrors.customerName}</div>
                        )}
                    </div>

                 {/* Contact Information */}

<div className="row mb-3 g-2">  
    <div className="col-6 col-sm-6 col-md-6">  
        {/* <label className="form-label">Mobile No</label> */}
        <input
            type="tel"
            pattern="[0-9]{10}"
            maxLength={10}
            className="form-control"
            placeholder="Mobile No"
            name="contactNo"
            value={customerMaster.contactNo}
            onChange={handleInputChange}
            required
        />
    </div>
    <div className="col-6 col-sm-6 col-md-6">  {/* Forces 50% width on all screens */}
        {/* <label className="form-label">Email Id</label> */}
        <input
            type="email"
            className="form-control"
            placeholder="Email Id"
            name="contactEmail"
            value={customerMaster.contactEmail}
            onChange={handleInputChange}
        />
    </div>
</div>

                    {/* Password Fields */}
               
<div className="row mb-3 g-2">
    <div className="col-6 col-md-6">
        {/* <label className="form-label">Password</label> */}
        <div className="input-group">
            <input
                type={showPassword ? "text" : "password"}
                className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                placeholder="Enter Password"
                name="password"
              
                value={customerMaster.password}
                onChange={handleInputChange}
            />
            <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
            >
                {showPassword ? <IoEyeSharp /> : <FaEyeSlash />}
            </button>
            {formErrors.password && (
                <div className="invalid-feedback">{formErrors.password}</div>
            )}
        </div>
    </div>
    <div className="col-6 col-md-6">
        {/* <label className="form-label">Confirm Password</label> */}
        <div className="input-group">
            <input
                type={showConfirmPassword ? "text" : "password"}
                className={`form-control ${formErrors.confirmPassword ? "is-invalid" : ""}`}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
            />
            <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
                {showConfirmPassword ? <IoEyeSharp /> : <FaEyeSlash />}
            </button>
            {formErrors.confirmPassword && (
                <div className="invalid-feedback">{formErrors.confirmPassword}</div>
            )}
        </div>
    </div>
</div>

                    {/* Location Fields */}
                    <div className="mb-3">
                        <label className="form-label">Location</label>
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="text"
                                style={{width:"50%"}}
                                className={`form-control ${formErrors.coordinates ? "is-invalid" : ""}`}
                                value={latitude && longitude ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : ""}
                                placeholder="Current location coordinates"
                                readOnly
                            />
                            <button
                                className="btn btn-outline-primary"
                                onClick={getCurrentLocation}
                                type="button"
                            >
                                <TbCurrentLocation size={24} />
                            </button>
                            <button
                                className="btn btn-primary flex-grow-1"
                                onClick={() => setShowMap(!showMap)}
                                type="button"
                                disabled = {!latitude}
                            >
                                {showMap ? "Close Map" : "Open Map"}
                            </button>
                        </div>
                        {formErrors.coordinates && (
                            <div className="invalid-feedback d-block">{formErrors.coordinates}</div>
                        )}
                    </div>

                    {showMap && (
                        <div className="mb-3">
                            <LocationPicker
                                latitude={latitude}
                                longitude={longitude}
                                setLatitude={setLatitude}
                                setLongitude={setLongitude}
                                fetchAddressFromCoordinates={fetchAddressFromCoordinates}
                            />
                        </div>
                    )}

                    {/* Address Fields */}
                    <div className="row g-2 mb-3">
    {/* Area Field */}
    <div className="col-6 col-md-6">
        {/* <label className="form-label">Area</label> */}
        <input
            type="text"
            className={`form-control ${formErrors.area ? "is-invalid" : ""}`}
            placeholder="Area"
            name="area"
            value={customerMaster.addresses[0].area}
            onChange={handleAddressChange}
        />
        {formErrors.area && (
            <div className="invalid-feedback d-block">{formErrors.area}</div>
        )}
    </div>

    {/* Address Line 1 Field */}
    <div className="col-6 col-md-6">
        {/* <label className="form-label">Address Line 1</label> */}
        <input
            type="text"
            className={`form-control ${formErrors.addLine1 ? "is-invalid" : ""}`}
            placeholder="Address Line 1"
            name="addLine1"
            value={customerMaster.addresses[0].addLine1}
            onChange={handleAddressChange}
        />
        {formErrors.addLine1 && (
            <div className="invalid-feedback d-block">{formErrors.addLine1}</div>
        )}
    </div>
</div>


                    <div className="row mb-3">
                        <div className="col-6 col-md-6">
                            {/* <label className="form-label">Address Line 2</label> */}
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Address Line 2"
                                name="addLine2"
                                value={customerMaster.addresses[0].addLine2}
                                onChange={handleAddressChange}
                            />
                        </div>
                        <div className="col-6 col-md-6">
                            {/* <label className="form-label">Nearby Landmark</label> */}
                            <input
                                type="text"
                                className={`form-control ${formErrors.nearBy ? "is-invalid" : ""}`}
                                placeholder="Near By"
                                name="nearBy"
                                value={customerMaster.addresses[0].nearBy}
                                onChange={handleAddressChange}
                            />
                            {formErrors.nearBy && (
                                <div className="invalid-feedback">{formErrors.nearBy}</div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-6 col-md-6">
                            {/* <label className="form-label">City</label> */}
                            <input
                                type="text"
                                className={`form-control ${formErrors.city ? "is-invalid" : ""}`}
                                placeholder="City"
                                name="city"
                                value={customerMaster.addresses[0].city}
                                onChange={handleAddressChange}
                            />
                            {formErrors.city && (
                                <div className="invalid-feedback">{formErrors.city}</div>
                            )}
                        </div>
                        <div className="col-6 col-md-6">
                            {/* <label className="form-label">State</label> */}
                            <input
                                type="text"
                                className="form-control"
                                placeholder="State"
                                name="state"
                                value={customerMaster.addresses[0].state}
                                onChange={handleAddressChange}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        {/* <label className="form-label">PinCode</label> */}
                        <input
                            type="text"
                            className="form-control"
                            placeholder="PinCode"
                            name="pincode"
                            value={customerMaster.addresses[0].pincode}
                            onChange={handleAddressChange}
                            style={{width: "190px"}}
                        />
                    </div>

                    {/* Submit Button */}
                    <div className="d-grid">
                        <button
                            className="btn btn-primary"
                            onClick={RegisterCustomer}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegCustomer;